import {
  Car,
  Droplets,
  Shovel,
  Wrench,
  Hammer,
  Leaf,
  Sparkles,
  Layers,
} from 'lucide-react'

export interface ProjectMaterial {
  slug: string
  role: string
  depth: string
  why: string
}

export interface ProjectFAQ {
  q: string
  a: string
}

export interface Project {
  slug: string
  name: string
  tagline: string
  icon: any // Lucide icon component
  /**
   * Optional job-site photo for the project card and guide header. Until a
   * real photo of THAT job exists, the card falls back to the photo of the
   * material the project leads with — so a project always has artwork, and
   * dropping a real one in later is a one-line change here.
   */
  image?: string
  intro: string
  typicalYards: string
  materials: ProjectMaterial[]
  tips: string[]
  faqs: ProjectFAQ[]
}

// English source-of-truth project content. Slugs MUST match between the
// English and Spanish arrays — they are the stable URL identifiers and
// keys used by Hero.tsx and pages/Project.tsx to find the right project.
export const projects: Project[] = [
  {
    slug: 'driveway-top-off',
    name: 'Driveway Top-Off',
    tagline: 'Refresh a thin, faded driveway with one clean layer.',
    icon: Layers,
    intro:
      "The most common gravel order we deliver isn't a new driveway — it's a top-off. If your driveway is still firm underfoot and just looks thin, faded, or patchy, you don't need to rebuild anything. One shallow layer of the same crushed rock over the existing surface restores the look and locks back together as it compacts. The mistake to avoid is ordering too deep: a top-off is 1–2 inches, not 4.",
    typicalYards: '4–10 yd',
    materials: [
      {
        slug: '5-8-minus',
        role: 'Top-Off Layer',
        depth: '1–2 in',
        why: 'The standard driveway surface rock — compacts tight over your existing base and matches what is already there.',
      },
      {
        slug: '3-8-minus-driveway',
        role: 'Smoother Finish',
        depth: '1–2 in',
        why: 'A finer alternative if you want a tighter, smoother surface underfoot and lighter traffic.',
      },
    ],
    tips: [
      'Order for 1–2 inches, not 4. A 1,000 sq ft driveway needs roughly 3–6 yards for a top-off — ordering base-layer depth means paying for triple the rock.',
      'Walk the driveway first. If it flexes, ruts, or holds water, the base has failed and a top-off will just sink — see the Muddy Driveway Fix guide instead.',
      'Match what is already down. Adding a different rock size on top can leave a loose layer that never knits into the surface below.',
      'Rake it out thin and even, then let traffic and rain compact it. A plate compactor helps on the first pass but is not required at this depth.',
      'Re-crown the center as you spread so water runs off the sides instead of sitting in the middle.',
    ],
    faqs: [
      {
        q: 'How much gravel do I need to top off my driveway?',
        a: 'For a 1–2 inch top-off, figure about 3 yards per 1,000 square feet per inch. A typical 12 x 80 ft driveway (960 sq ft) takes roughly 3 yards at 1 inch or 6 yards at 2 inches. Our calculator will do the math if you have your length and width.',
      },
      {
        q: 'What gravel should I use to top off an existing driveway?',
        a: '5/8" Minus is the standard choice — it is the same crushed rock most Puget Sound driveways are surfaced with, so it blends in and compacts tightly. Use 3/8" Minus if you want a finer, smoother finish.',
      },
      {
        q: 'How often should a gravel driveway be topped off?',
        a: 'Most driveways in our wet climate need a refresh every 2 to 4 years. Heavy truck traffic, steep grades, and driveways without a crown wear through faster.',
      },
      {
        q: 'Can I top off with a different size than what is already there?',
        a: 'You can, but stick with another minus (crushed with fines) so it locks in. Putting clear or washed rock on top of a minus driveway leaves loose stone that never binds and will migrate under tires.',
      },
      {
        q: 'My driveway has potholes. Can I just top off over them?',
        a: 'No. Spreading a thin layer over a pothole leaves a soft spot that reopens within weeks. Dig the hole out slightly, fill it with 1.25" Minus and compact, then top off the whole driveway.',
      },
    ],
  },
  {
    slug: 'new-gravel-driveway',
    name: 'New Gravel Driveway',
    tagline: 'Build a solid, long-lasting driveway from the dirt up.',
    icon: Car,
    intro:
      'Creating a new gravel driveway in the Pacific Northwest requires a solid foundation to handle our wet winters and heavy vehicle traffic. The key is using a coarse base layer for structural stability, topped with a finer gravel that locks together for a smooth driving surface.',
    typicalYards: '15–25 yd',
    materials: [
      {
        slug: '1-25-minus',
        role: 'Base Layer',
        depth: '4–6 in',
        why: 'Provides essential structural support and stability over bare dirt.',
      },
      {
        slug: '5-8-minus',
        role: 'Top Layer',
        depth: '2–3 in',
        why: 'Compacts tightly to create a smooth, durable driving surface.',
      },
    ],
    tips: [
      'Always compact the bare dirt subgrade before adding any rock.',
      'Compact the 1.25" Minus base layer thoroughly before adding the top layer.',
      'Crown the center of the driveway slightly so water runs off the sides.',
    ],
    faqs: [
      {
        q: 'Do I really need two different sizes of gravel?',
        a: 'Yes. The larger 1.25" rock provides the load-bearing strength, while the 5/8" rock fills in the gaps and creates a smooth surface. Using only 5/8" on dirt can lead to sinking and potholes.',
      },
      {
        q: 'Should I use landscape fabric under the driveway?',
        a: 'We highly recommend using a heavy-duty woven geotextile fabric between the dirt and your base rock. It prevents the rock from sinking into the mud over time.',
      },
      {
        q: 'How much gravel do I need for a standard driveway?',
        a: 'A typical 20x40 foot parking pad needs about 10 yards of base rock (4" deep) and 5 yards of top rock (2" deep).',
      },
    ],
  },
  {
    slug: 'muddy-driveway-fix',
    name: 'Muddy Driveway Fix',
    tagline: 'Rescue your sinking driveway from the PNW mud.',
    icon: Droplets,
    intro:
      "When your existing gravel driveway turns into a muddy mess, simply adding more fine gravel won't fix it—it will just sink into the mud. You need larger, angular rock to bridge the soft spots and re-establish a solid base before topping it off.",
    typicalYards: '10–20 yd',
    materials: [
      {
        slug: '2-4-quarry-rock',
        role: 'Mud Bridge (Severe)',
        depth: 'As needed',
        why: "Use in deep mud holes to create a solid bridge that won't sink.",
      },
      {
        slug: '1-25-minus',
        role: 'Base Repair',
        depth: '3–4 in',
        why: 'Fills in ruts and stabilizes the overall driveway surface.',
      },
      {
        slug: '5-8-minus',
        role: 'Top Dress',
        depth: '1–2 in',
        why: 'Restores a clean, smooth finish once the base is stabilized.',
      },
    ],
    tips: [
      'Don\'t just fill potholes with 5/8" minus; dig them out slightly and fill with larger 1.25" rock first.',
      'Ensure water has a place to drain off the driveway; standing water is the enemy of gravel.',
      'For extremely soft, bottomless mud, start with 2-4" quarry spalls to build a foundation.',
    ],
    faqs: [
      {
        q: 'Why does my driveway keep getting muddy every winter?',
        a: 'Usually, it means the original gravel has pushed down into the subsoil, or water is pooling on the surface. You need to build the base back up and improve drainage.',
      },
      {
        q: 'Can I just put 5/8" Minus over the mud?',
        a: 'No. Fine gravel will quickly mix with the mud and disappear. You must use larger rock (1.25" or Quarry Spalls) to bridge the soft spots first.',
      },
      {
        q: 'Do I need to grade the driveway before adding rock?',
        a: 'If possible, yes. Grading smooths out existing ruts and helps re-establish a crown for water runoff.',
      },
    ],
  },
  {
    slug: 'french-drain',
    name: 'French Drain',
    tagline: 'Manage standing water and protect your foundation.',
    icon: Wrench,
    intro:
      'A French drain is the most effective way to move unwanted water away from your home\'s foundation or low spots in your yard. The key is using a clean, washed rock with no "fines" (dust or sand) so water can flow freely into the perforated pipe.',
    typicalYards: '5–15 yd',
    materials: [
      {
        slug: '5-8-clear',
        role: 'Drainage Rock',
        depth: 'Varies',
        why: 'Washed clean of fines, allowing maximum water flow around the pipe.',
      },
    ],
    tips: [
      'Always line your trench with non-woven geotextile filter fabric to prevent soil from clogging the rock.',
      'Ensure your trench has a consistent downward slope (at least 1% or 1 inch drop per 8 feet).',
      'Place the perforated pipe with the holes facing DOWN, not up.',
    ],
    faqs: [
      {
        q: 'What is the difference between 5/8" Clear and 5/8" Minus?',
        a: '5/8" Clear is washed to remove all dust and small particles, allowing water to flow through. 5/8" Minus contains dust (fines) designed to pack tightly and block water.',
      },
      {
        q: 'Can I use Pea Gravel for a French drain?',
        a: 'While pea gravel drains well, 5/8" Crushed Clear is preferred because the angular edges lock together, providing better structural stability in the trench.',
      },
      {
        q: 'How deep should a French drain be?',
        a: 'Typically 18 to 24 inches deep, but it depends on what you are trying to drain. Surface water needs a shallower drain than groundwater.',
      },
    ],
  },
  {
    slug: 'paver-patio-base',
    name: 'Paver Patio Base',
    tagline: 'The hidden foundation for a perfect, level patio.',
    icon: Hammer,
    intro:
      'A beautiful paver patio is only as good as the base underneath it. In our freeze-thaw climate, a proper compacted gravel base prevents pavers from shifting, sinking, or heaving over time. You need a structural base topped with a thin leveling layer.',
    typicalYards: '5–10 yd',
    materials: [
      {
        slug: '5-8-minus',
        role: 'Structural Base',
        depth: '4–6 in',
        why: "Compacts into a rock-hard foundation that won't settle.",
      },
      {
        slug: 'utility-sand',
        role: 'Leveling Layer',
        depth: '1 in',
        why: 'Provides a smooth, screedable surface to set the pavers into.',
      },
    ],
    tips: [
      'Excavate deep enough to accommodate your base rock, sand, and the thickness of the pavers.',
      'Compact the 5/8" Minus base in 2-inch layers (lifts) for maximum density.',
      'Do not use sand to fix dips in your gravel base; the sand layer must be a uniform 1 inch thick.',
    ],
    faqs: [
      {
        q: 'Can I just lay pavers on sand or dirt?',
        a: 'No. Without a compacted gravel base, your pavers will quickly become uneven, sink, and separate.',
      },
      {
        q: 'Why use 5/8" Minus instead of 1.25" Minus for the base?',
        a: 'For pedestrian patios, 5/8" Minus is easier to grade smoothly by hand while still providing excellent compaction. 1.25" is better for heavy vehicular loads.',
      },
      {
        q: 'How much sand do I need?',
        a: 'Plan for exactly 1 inch of sand across the entire patio area. 1 yard of sand covers about 324 square feet at 1 inch deep.',
      },
    ],
  },
  {
    slug: 'retaining-wall-backfill',
    name: 'Retaining Wall Backfill',
    tagline: 'Relieve hydrostatic pressure and prevent wall failure.',
    icon: Shovel,
    intro:
      'The number one cause of retaining wall failure is water pressure building up behind the wall. Proper backfill using clean, draining rock is crucial to direct water down to a drain pipe and away from the wall structure.',
    typicalYards: '10–20 yd',
    materials: [
      {
        slug: '5-8-clear',
        role: 'Drainage Backfill',
        depth: '12+ in behind wall',
        why: 'Allows water to drain instantly, removing pressure from the wall.',
      },
      {
        slug: '5-8-minus',
        role: 'Leveling Pad',
        depth: '6 in (under wall)',
        why: 'Creates a solid, compacted footing for the first course of blocks.',
      },
    ],
    tips: [
      'Backfill with at least 12 inches of clear rock directly behind the wall blocks.',
      'Install a perforated drain pipe at the base of the clear rock backfill to carry water away.',
      'Separate the clear rock backfill from the native soil behind it using filter fabric.',
    ],
    faqs: [
      {
        q: 'Can I just use the dirt I dug out to backfill the wall?',
        a: 'No. Native soil (especially clay) holds water and expands, creating massive pressure that will eventually push your wall over.',
      },
      {
        q: 'What goes under the first row of blocks?',
        a: 'A compacted leveling pad of 5/8" Minus gravel, typically 6 inches deep and twice as wide as the block.',
      },
      {
        q: 'Do I need a drain pipe if I use clear rock?',
        a: 'Yes. The clear rock allows water to drop to the bottom of the wall, but the pipe is needed to carry that water away from the area.',
      },
    ],
  },
  {
    slug: 'pea-gravel-path',
    name: 'Pea Gravel Patio/Path',
    tagline: 'Create a soft, natural walkway or seating area.',
    icon: Sparkles,
    intro:
      "Pea gravel offers a classic, soft aesthetic that crunches pleasantly underfoot. It's an affordable and easy-to-install option for garden paths, fire pit areas, and casual patios. Because the stones are round, they don't compact, so proper containment is key.",
    typicalYards: '3–8 yd',
    materials: [
      {
        slug: 'pea-gravel',
        role: 'Surface Layer',
        depth: '2–3 in',
        why: 'Provides a decorative, comfortable surface that drains instantly.',
      },
      {
        slug: '5-8-minus',
        role: 'Optional Base',
        depth: '2–3 in',
        why: 'Creates a firm foundation under the pea gravel in soft soil areas.',
      },
    ],
    tips: [
      'Always use rigid edging (wood, metal, or stone) to keep pea gravel from migrating into your lawn.',
      'Keep the pea gravel layer to 2-3 inches deep; any deeper and it becomes difficult to walk through (like walking in deep sand).',
      'Use landscape fabric underneath to prevent weeds and keep the gravel from sinking into the mud.',
    ],
    faqs: [
      {
        q: 'Is pea gravel good for a driveway?',
        a: 'No. Because the stones are round, they never compact. Cars will sink into it and push it around.',
      },
      {
        q: 'How do I keep weeds out of my pea gravel path?',
        a: 'Install a high-quality woven landscape fabric before laying the gravel, and occasionally use a weed torch or natural herbicide for seeds that blow in from above.',
      },
      {
        q: 'Do I need a compacted base under pea gravel?',
        a: 'For light foot traffic on firm soil, you can lay it directly over fabric. For softer soil or heavier use, a 2-3 inch compacted base of 5/8" Minus helps stabilize the area.',
      },
    ],
  },
  {
    slug: 'beauty-bark-refresh',
    name: 'Beauty Bark Refresh',
    tagline: 'Instantly boost curb appeal and suppress weeds.',
    icon: Leaf,
    intro:
      'A fresh layer of bark mulch is the quickest way to make your landscaping look professionally maintained. Beyond aesthetics, bark retains soil moisture during our dry PNW summers and provides a natural barrier against weed growth.',
    typicalYards: '5–15 yd',
    materials: [
      {
        slug: 'medium-bark',
        role: 'Ground Cover',
        depth: '2–3 in',
        why: 'The PNW standard. Knits together well to resist blowing or washing away.',
      },
    ],
    tips: [
      'Apply bark 2-3 inches deep for effective weed suppression and moisture retention.',
      'Keep bark pulled back a few inches from the base of trees and shrubs to prevent rot.',
      'Weed the area thoroughly before applying new bark.',
    ],
    faqs: [
      {
        q: 'How often do I need to replace bark?',
        a: 'Most homeowners do a "top dress" of 1-2 inches every 1 to 2 years to maintain color and thickness as the old bark breaks down.',
      },
      {
        q: 'Should I put plastic or fabric under the bark?',
        a: 'We generally advise against plastic, as it prevents water and nutrients from reaching the soil. Bark alone, applied thick enough, is an excellent weed deterrent.',
      },
      {
        q: 'Fine bark vs. Medium bark?',
        a: 'Fine bark looks more manicured but breaks down faster. Medium bark lasts longer and is better for slopes or windy areas.',
      },
    ],
  },
]

// Legacy Spanish project copy is intentionally private and unreachable in
// the English-only storefront, allowing production builds to omit it.
const projectsEs: Project[] = [
  {
    slug: 'driveway-top-off',
    name: 'Renovar Entrada de Grava',
    tagline: 'Refresca una entrada delgada o desgastada con una sola capa.',
    icon: Layers,
    intro:
      'El pedido más común que entregamos no es una entrada nueva, sino una capa de renovación. Si tu entrada sigue firme y solo se ve delgada o desgastada, no necesitas reconstruir nada: una capa delgada de la misma roca triturada restaura la superficie. El error a evitar es pedir demasiada profundidad — una renovación es de 1 a 2 pulgadas, no 4.',
    typicalYards: '4–10 yardas',
    materials: [
      {
        slug: '5-8-minus',
        role: 'Capa de Renovación',
        depth: '1–2 pulg',
        why: 'La roca estándar para superficies de entrada — se compacta firmemente sobre la base existente.',
      },
      {
        slug: '3-8-minus-driveway',
        role: 'Acabado Más Fino',
        depth: '1–2 pulg',
        why: 'Una alternativa más fina si quieres una superficie más lisa para tráfico ligero.',
      },
    ],
    tips: [
      'Pide para 1–2 pulgadas, no 4. Una entrada de 1,000 pies cuadrados necesita entre 3 y 6 yardas.',
      'Camina la entrada primero. Si se hunde o retiene agua, la base falló y necesitas la guía de Reparación de Entrada Lodosa.',
      'Usa el mismo tamaño de roca que ya tienes para que la capa nueva se integre.',
      'Extiende la capa delgada y uniforme, y deja que el tráfico y la lluvia la compacten.',
      'Vuelve a formar la corona del centro para que el agua escurra por los lados.',
    ],
    faqs: [
      {
        q: '¿Cuánta grava necesito para renovar mi entrada?',
        a: 'Para una capa de 1–2 pulgadas, calcula unas 3 yardas por cada 1,000 pies cuadrados por pulgada.',
      },
      {
        q: '¿Qué grava debo usar para renovar una entrada existente?',
        a: '5/8" Minus es la opción estándar porque se compacta firmemente y coincide con la mayoría de las entradas de la región.',
      },
      {
        q: '¿Con qué frecuencia se debe renovar una entrada de grava?',
        a: 'En nuestro clima húmedo, la mayoría necesita renovación cada 2 a 4 años.',
      },
    ],
  },
  {
    slug: 'new-gravel-driveway',
    name: 'Nueva Entrada de Grava',
    tagline: 'Construye una entrada sólida y duradera desde la tierra.',
    icon: Car,
    intro:
      'Crear una nueva entrada de grava en el noroeste del Pacífico requiere una base sólida para soportar nuestros inviernos húmedos y el tráfico vehicular pesado. La clave es usar una capa base gruesa para estabilidad estructural, cubierta con una grava más fina que se compacta para ofrecer una superficie de manejo lisa.',
    typicalYards: '15–25 yardas',
    materials: [
      {
        slug: '1-25-minus',
        role: 'Capa Base',
        depth: '4–6 pulg',
        why: 'Proporciona soporte estructural esencial y estabilidad sobre la tierra desnuda.',
      },
      {
        slug: '5-8-minus',
        role: 'Capa Superior',
        depth: '2–3 pulg',
        why: 'Se compacta firmemente para crear una superficie de manejo lisa y duradera.',
      },
    ],
    tips: [
      'Siempre compacta la subbase de tierra desnuda antes de agregar piedra.',
      'Compacta bien la capa base de 1.25" Minus antes de añadir la capa superior.',
      'Forma una ligera corona en el centro de la entrada para que el agua escurra por los lados.',
    ],
    faqs: [
      {
        q: '¿Realmente necesito dos tamaños diferentes de grava?',
        a: 'Sí. La roca más grande de 1.25" proporciona la resistencia de carga, mientras que la de 5/8" llena los huecos y crea una superficie lisa. Usar solo 5/8" sobre tierra puede provocar hundimientos y baches.',
      },
      {
        q: '¿Debo usar tela de jardín bajo la entrada?',
        a: 'Recomendamos encarecidamente usar una tela geotextil tejida resistente entre la tierra y tu roca base. Evita que la roca se hunda en el lodo con el tiempo.',
      },
      {
        q: '¿Cuánta grava necesito para una entrada estándar?',
        a: 'Una plataforma típica de 20x40 pies necesita aproximadamente 10 yardas de roca base (4" de profundidad) y 5 yardas de roca superior (2" de profundidad).',
      },
    ],
  },
  {
    slug: 'muddy-driveway-fix',
    name: 'Reparación de Entrada Lodosa',
    tagline: 'Rescata tu entrada que se hunde del lodo del PNW.',
    icon: Droplets,
    intro:
      'Cuando tu entrada de grava existente se convierte en un desastre lodoso, simplemente agregar más grava fina no lo solucionará — solo se hundirá en el lodo. Necesitas roca angular más grande para puentear los puntos blandos y restablecer una base sólida antes de cubrirla.',
    typicalYards: '10–20 yardas',
    materials: [
      {
        slug: '2-4-quarry-rock',
        role: 'Puente de Lodo (Severo)',
        depth: 'Según necesidad',
        why: 'Úsalo en agujeros de lodo profundos para crear un puente sólido que no se hunda.',
      },
      {
        slug: '1-25-minus',
        role: 'Reparación de Base',
        depth: '3–4 pulg',
        why: 'Rellena las roderas y estabiliza la superficie general de la entrada.',
      },
      {
        slug: '5-8-minus',
        role: 'Capa Final',
        depth: '1–2 pulg',
        why: 'Restaura un acabado limpio y liso una vez que la base está estabilizada.',
      },
    ],
    tips: [
      'No rellenes baches con 5/8" minus; excávalos un poco y rellena primero con roca de 1.25".',
      'Asegúrate de que el agua tenga dónde drenar; el agua estancada es enemiga de la grava.',
      'Para lodo extremadamente blando, comienza con piedras de cantera de 2-4" para construir una base.',
    ],
    faqs: [
      {
        q: '¿Por qué mi entrada se enloda cada invierno?',
        a: 'Normalmente significa que la grava original se ha hundido en el subsuelo, o que el agua se está acumulando en la superficie. Necesitas reconstruir la base y mejorar el drenaje.',
      },
      {
        q: '¿Puedo simplemente poner 5/8" Minus sobre el lodo?',
        a: 'No. La grava fina rápidamente se mezclará con el lodo y desaparecerá. Debes usar roca más grande (1.25" o Quarry Spalls) para puentear los puntos blandos primero.',
      },
      {
        q: '¿Necesito nivelar la entrada antes de agregar roca?',
        a: 'Si es posible, sí. Nivelar suaviza las roderas existentes y ayuda a restablecer una corona para el escurrimiento del agua.',
      },
    ],
  },
  {
    slug: 'french-drain',
    name: 'Drenaje Francés',
    tagline: 'Maneja el agua estancada y protege tu cimiento.',
    icon: Wrench,
    intro:
      'Un drenaje francés es la forma más efectiva de alejar el agua no deseada de los cimientos de tu casa o de los puntos bajos en tu jardín. La clave es usar una roca limpia y lavada sin "finos" (polvo o arena) para que el agua pueda fluir libremente hacia el tubo perforado.',
    typicalYards: '5–15 yardas',
    materials: [
      {
        slug: '5-8-clear',
        role: 'Roca de Drenaje',
        depth: 'Variable',
        why: 'Lavada sin finos, permitiendo el flujo máximo de agua alrededor del tubo.',
      },
    ],
    tips: [
      'Siempre forra tu zanja con tela geotextil no tejida para evitar que la tierra obstruya la roca.',
      'Asegura una pendiente descendente consistente (al menos 1% o 1 pulgada de caída por cada 8 pies).',
      'Coloca el tubo perforado con los agujeros HACIA ABAJO, no hacia arriba.',
    ],
    faqs: [
      {
        q: '¿Cuál es la diferencia entre 5/8" Clear y 5/8" Minus?',
        a: '5/8" Clear está lavada para eliminar todo el polvo y las partículas pequeñas, permitiendo que el agua fluya. 5/8" Minus contiene polvo (finos) diseñado para compactarse y bloquear el agua.',
      },
      {
        q: '¿Puedo usar Pea Gravel para un drenaje francés?',
        a: 'Aunque la pea gravel drena bien, se prefiere la 5/8" Crushed Clear porque sus bordes angulares se entrelazan, ofreciendo mejor estabilidad estructural en la zanja.',
      },
      {
        q: '¿Qué tan profundo debe ser un drenaje francés?',
        a: 'Típicamente de 18 a 24 pulgadas de profundidad, pero depende de lo que estés tratando de drenar. El agua superficial necesita un drenaje menos profundo que el agua subterránea.',
      },
    ],
  },
  {
    slug: 'paver-patio-base',
    name: 'Base de Patio de Adoquines',
    tagline: 'El cimiento oculto para un patio perfecto y nivelado.',
    icon: Hammer,
    intro:
      'Un hermoso patio de adoquines es solo tan bueno como la base que tiene debajo. En nuestro clima de congelamiento y descongelamiento, una base de grava bien compactada evita que los adoquines se muevan, se hundan o se levanten con el tiempo. Necesitas una base estructural cubierta con una capa delgada de nivelación.',
    typicalYards: '5–10 yardas',
    materials: [
      {
        slug: '5-8-minus',
        role: 'Base Estructural',
        depth: '4–6 pulg',
        why: 'Se compacta en un cimiento sólido como roca que no se asentará.',
      },
      {
        slug: 'utility-sand',
        role: 'Capa de Nivelación',
        depth: '1 pulg',
        why: 'Proporciona una superficie lisa y nivelable para asentar los adoquines.',
      },
    ],
    tips: [
      'Excava lo suficientemente profundo para acomodar tu roca base, arena y el grosor de los adoquines.',
      'Compacta la base de 5/8" Minus en capas de 2 pulgadas (lifts) para máxima densidad.',
      'No uses arena para corregir hundimientos en tu base de grava; la capa de arena debe ser un grosor uniforme de 1 pulgada.',
    ],
    faqs: [
      {
        q: '¿Puedo simplemente poner adoquines sobre arena o tierra?',
        a: 'No. Sin una base de grava compactada, tus adoquines rápidamente se volverán desiguales, se hundirán y se separarán.',
      },
      {
        q: '¿Por qué usar 5/8" Minus en lugar de 1.25" Minus para la base?',
        a: 'Para patios peatonales, 5/8" Minus es más fácil de nivelar suavemente a mano y aun así ofrece excelente compactación. 1.25" es mejor para cargas vehiculares pesadas.',
      },
      {
        q: '¿Cuánta arena necesito?',
        a: 'Planea exactamente 1 pulgada de arena en toda el área del patio. 1 yarda de arena cubre aproximadamente 324 pies cuadrados a 1 pulgada de profundidad.',
      },
    ],
  },
  {
    slug: 'retaining-wall-backfill',
    name: 'Relleno de Muro de Contención',
    tagline: 'Alivia la presión hidrostática y previene la falla del muro.',
    icon: Shovel,
    intro:
      'La causa número uno de fallas en muros de contención es la presión del agua acumulándose detrás del muro. Un relleno adecuado con roca limpia y drenante es crucial para dirigir el agua hacia un tubo de drenaje y alejarla de la estructura del muro.',
    typicalYards: '10–20 yardas',
    materials: [
      {
        slug: '5-8-clear',
        role: 'Relleno de Drenaje',
        depth: '12+ pulg detrás del muro',
        why: 'Permite que el agua drene al instante, quitando la presión del muro.',
      },
      {
        slug: '5-8-minus',
        role: 'Plataforma de Nivelación',
        depth: '6 pulg (bajo el muro)',
        why: 'Crea un cimiento sólido y compactado para la primera hilada de bloques.',
      },
    ],
    tips: [
      'Rellena con al menos 12 pulgadas de roca limpia directamente detrás de los bloques del muro.',
      'Instala un tubo de drenaje perforado en la base del relleno de roca limpia para llevar el agua lejos.',
      'Separa el relleno de roca limpia del suelo nativo detrás usando tela filtrante.',
    ],
    faqs: [
      {
        q: '¿Puedo usar la misma tierra que excavé para rellenar el muro?',
        a: 'No. El suelo nativo (especialmente la arcilla) retiene agua y se expande, creando una presión masiva que eventualmente empujará tu muro.',
      },
      {
        q: '¿Qué va debajo de la primera fila de bloques?',
        a: 'Una plataforma de nivelación compactada de grava 5/8" Minus, típicamente 6 pulgadas de profundidad y el doble de ancha que el bloque.',
      },
      {
        q: '¿Necesito un tubo de drenaje si uso roca limpia?',
        a: 'Sí. La roca limpia permite que el agua llegue al fondo del muro, pero el tubo es necesario para llevar esa agua lejos del área.',
      },
    ],
  },
  {
    slug: 'pea-gravel-path',
    name: 'Patio/Camino de Pea Gravel',
    tagline: 'Crea un camino o área de descanso suave y natural.',
    icon: Sparkles,
    intro:
      'La pea gravel ofrece una estética clásica y suave que cruje agradablemente bajo los pies. Es una opción económica y fácil de instalar para senderos de jardín, áreas de fogata y patios casuales. Como las piedras son redondas, no se compactan, así que una contención adecuada es clave.',
    typicalYards: '3–8 yardas',
    materials: [
      {
        slug: 'pea-gravel',
        role: 'Capa Superficial',
        depth: '2–3 pulg',
        why: 'Proporciona una superficie decorativa y cómoda que drena al instante.',
      },
      {
        slug: '5-8-minus',
        role: 'Base Opcional',
        depth: '2–3 pulg',
        why: 'Crea un cimiento firme bajo la pea gravel en áreas de suelo blando.',
      },
    ],
    tips: [
      'Siempre usa bordes rígidos (madera, metal o piedra) para evitar que la pea gravel migre hacia tu césped.',
      'Mantén la capa de pea gravel a 2-3 pulgadas de profundidad; cualquier capa más profunda se vuelve difícil de caminar (como caminar en arena profunda).',
      'Usa tela de jardín debajo para prevenir maleza y evitar que la grava se hunda en el lodo.',
    ],
    faqs: [
      {
        q: '¿Es buena la pea gravel para una entrada de auto?',
        a: 'No. Como las piedras son redondas, nunca se compactan. Los autos se hundirán y la moverán por todos lados.',
      },
      {
        q: '¿Cómo mantengo la maleza fuera de mi camino de pea gravel?',
        a: 'Instala una tela de jardín tejida de alta calidad antes de poner la grava, y ocasionalmente usa una antorcha de maleza o un herbicida natural para semillas que vuelan desde arriba.',
      },
      {
        q: '¿Necesito una base compactada bajo la pea gravel?',
        a: 'Para tráfico peatonal ligero en suelo firme, puedes ponerla directamente sobre tela. Para suelo más blando o uso más pesado, una base compactada de 2-3 pulgadas de 5/8" Minus ayuda a estabilizar el área.',
      },
    ],
  },
  {
    slug: 'beauty-bark-refresh',
    name: 'Renovación de Beauty Bark',
    tagline: 'Mejora instantáneamente el atractivo y suprime la maleza.',
    icon: Leaf,
    intro:
      'Una capa fresca de mantillo de corteza es la forma más rápida de hacer que tu paisajismo se vea mantenido profesionalmente. Más allá de la estética, la corteza retiene la humedad del suelo durante nuestros secos veranos del PNW y proporciona una barrera natural contra el crecimiento de maleza.',
    typicalYards: '5–15 yardas',
    materials: [
      {
        slug: 'medium-bark',
        role: 'Cubierta de Suelo',
        depth: '2–3 pulg',
        why: 'El estándar del PNW. Se entrelaza bien para resistir el viento o la lluvia.',
      },
    ],
    tips: [
      'Aplica corteza a 2-3 pulgadas de profundidad para una supresión efectiva de maleza y retención de humedad.',
      'Mantén la corteza separada algunas pulgadas de la base de árboles y arbustos para prevenir pudrición.',
      'Deshierba el área a fondo antes de aplicar nueva corteza.',
    ],
    faqs: [
      {
        q: '¿Con qué frecuencia debo reemplazar la corteza?',
        a: 'La mayoría de los propietarios hace una "capa superficial" de 1-2 pulgadas cada 1 a 2 años para mantener color y grosor a medida que la corteza vieja se descompone.',
      },
      {
        q: '¿Debo poner plástico o tela debajo de la corteza?',
        a: 'Generalmente desaconsejamos el plástico, ya que impide que el agua y los nutrientes lleguen al suelo. La corteza sola, aplicada lo suficientemente gruesa, es un excelente disuasivo de maleza.',
      },
      {
        q: '¿Bark fino vs. Bark mediano?',
        a: 'El bark fino se ve más manicurado pero se descompone más rápido. El bark mediano dura más y es mejor para pendientes o áreas ventosas.',
      },
    ],
  },
]

// Real job-site photos, keyed by slug so the English and Spanish copies of a
// project stay on the same image without repeating the URL in both arrays.
// Adding a slug here is all it takes to give a project its own artwork.
const projectImages: Record<string, string> = {
  'driveway-top-off':
    'https://cdn.magicpatterns.com/uploads/1guDgwrtkbewff8iSjS6X2/Driveway_top_off.jpg',
  'new-gravel-driveway':
    'https://cdn.magicpatterns.com/uploads/nDyTRmRym7y7zDbAB57d1D/new_gravel_driveway.jpg',
  'muddy-driveway-fix':
    'https://cdn.magicpatterns.com/uploads/9NTHpBZau6qHY2owMYQSMk/muddy_driveway_fix.jpg',
  'french-drain':
    'https://cdn.magicpatterns.com/uploads/dQeakhy1KCHeC6xnJLBWAA/french_drain.jpg',
  'paver-patio-base':
    'https://cdn.magicpatterns.com/uploads/5mgDGsKLYRBNwfi7kKwHgN/paver_patio_base.jpg',
  'retaining-wall-backfill':
    'https://cdn.magicpatterns.com/uploads/6CGogwnEiZckWj798W6hnW/retaining_wall_backfill.jpg',
  'pea-gravel-path':
    'https://cdn.magicpatterns.com/uploads/1CYmZDVdpgDqdnNr19h2Dq/pea_gravel_patio_path.jpg',
  'beauty-bark-refresh':
    'https://cdn.magicpatterns.com/uploads/tEn9iao9iGDqqEh6yvoVzx/beauty_bark_refresh.jpg',
}

for (const list of [projects, projectsEs]) {
  for (const project of list) {
    const image = projectImages[project.slug]
    if (image) project.image = image
  }
}

// English-only storefront project list.
export function getProjects(): Project[] {
  return projects
}
